import { AxiosError } from 'axios'

import { logService } from '@/services/log-service'

interface ValidationError {
  errorCode: string
  errorMessage: string
  propertyName: string
}

interface IdentityError {
  code: string
  description: string
}

// Machine-readable codes the auth endpoints return in 400 {code, description} and 403 {code, message} bodies.
export const AuthErrorCode = {
  StepUpRequired: 'StepUpRequired',
  TwoFactorRequired: 'TwoFactorRequired',
  EnforcementActive: 'EnforcementActive',
  ResetPasswordRequired: 'ResetPasswordRequired',
  MethodChangeNotAllowed: 'MethodChangeNotAllowed',
  NoTwoFactorMethod: 'NoTwoFactorMethod',
  MethodNotSetUp: 'MethodNotSetUp',
  EmailRetired: 'EmailRetired',
  AuthenticatorUnavailable: 'AuthenticatorUnavailable',
  SupportPhonePinned: 'SupportPhonePinned',
  ImpersonatedSession: 'ImpersonatedSession',
  CodeAlreadyUsed: 'CodeAlreadyUsed',
  InvalidCode: 'InvalidCode',
  RecoveryCodesExhausted: 'RecoveryCodesExhausted',
  RateLimited: 'RateLimited',
} as const

export type AuthErrorCode = (typeof AuthErrorCode)[keyof typeof AuthErrorCode]

const ACCOUNT_LOCKED_MESSAGE = 'Account Locked'

const MAX_ERROR_MESSAGE_LINES = 10

function joinCapped(lines: string[]) {
  const extra = lines.length - MAX_ERROR_MESSAGE_LINES
  return lines.slice(0, MAX_ERROR_MESSAGE_LINES).join('\n') + (extra > 0 ? `\n…and ${extra} more` : '')
}

function getResponseData(error: unknown) {
  if (!(error instanceof AxiosError) || !error.response) return undefined
  return error.response.data as Record<string, unknown> | string | undefined
}

function getErrorStatus(error: unknown) {
  return error instanceof AxiosError ? error.response?.status : undefined
}

// 403 bodies come back as { code, message }, 400 bodies as { Code, Description } - read both spellings.
function readString(data: Record<string, unknown> | string | undefined, ...keys: string[]) {
  if (!data || typeof data !== 'object') return undefined

  for (const key of keys) {
    const value = data[key]
    if (typeof value === 'string' && value) return value
  }

  return undefined
}

function getErrorDescription(data: Record<string, unknown> | string | undefined) {
  return readString(data, 'description', 'Description')
}

function getErrorCode(error: unknown) {
  return readString(getResponseData(error), 'code', 'Code')
}

export function isErrorCode(error: unknown, code: AuthErrorCode) {
  return getErrorCode(error) === code
}

export function isStepUpRequiredError(error: unknown) {
  return getErrorStatus(error) === 403 && isErrorCode(error, AuthErrorCode.StepUpRequired)
}

// The limiter answers 429 with its own message, which beats any wording the caller would fall back to.
export function isRateLimitedError(error: unknown) {
  return getErrorStatus(error) === 429 && isErrorCode(error, AuthErrorCode.RateLimited)
}

export function isAccountLockedError(error: unknown) {
  return getErrorStatus(error) === 401 && getResponseData(error) === ACCOUNT_LOCKED_MESSAGE
}

export function getErrorMessage(error: unknown) {
  let message = ''

  if (error instanceof AxiosError) {
    if (error.response) {
      const data = error.response.data as Record<string, unknown> | string | undefined
      const description = getErrorDescription(data)

      if (typeof data === 'string') {
        message = data
      } else if (data?.title) {
        if (data?.errors && typeof data.errors === 'object' && !Array.isArray(data.errors)) {
          message = joinCapped(
            Object.values(data.errors)
              .flat()
              .filter((v): v is string => typeof v === 'string'),
          )
        } else {
          message = data.title as string
        }
      } else if (data?.message) {
        message = data.message as string
      } else if (description) {
        message = description
      } else if (Array.isArray(data?.errors)) {
        message = joinCapped(data.errors.map((e: ValidationError) => e.errorMessage?.trim()).filter(Boolean))
      } else if (Array.isArray(data)) {
        message = joinCapped(data.map((e: IdentityError) => e.description?.trim()).filter(Boolean))
      } else {
        message = error.message
      }
    } else {
      message = error.message
    }
  } else if (error instanceof Error) {
    message = error.message
  } else if (typeof error === 'string') {
    message = error
  }

  if (!message) {
    logService.error('Unhandled error format', { error })
  }

  return message || 'Unexpected error encountered. Please try again.'
}
